<template>
  <div class="dg" id="stripe-modal-container">
    <div class="stripe-modal-body">
      <div class="dg flex justify-content-between align-items-center">
        <div class="stripe-modal-heading">Set up your Stripe account to collect payments</div>
        <i class="fal fa-times exit" @click="closeModal()"></i>
      </div>

      <div>
        <form @submit.prevent="handleSubmit" >
          <div class="description">
            <p>Rallyhood has chosen Stripe as its payments processing platform because of its best-in-class security tools and certifications, as well as its years as the market-leader, empowering millions of business and individuals to collect and make payments</p>
            <p>Once you set up your Strip account, you can collect money for Events, Dues, or Fundraising and more in Rallyhood.</p>

            <p>Read for more information about <a target="_blank"
                href="https://stripe.com/docs/security/stripe">Stripe's Security Certification </a> and more.</p>
          </div>

          <!-- Import picker first: shown from the moment the modal opens (with a
               loading state until the user's accounts arrive) so people who
               already have a Stripe account never start the new-account form. -->
          <div v-if="loading || filteredUserAccounts.length > 0" class="form-group" style="margin-top: 25px;">
            <label><strong>Import an existing Stripe Account</strong></label>
            <select v-model="selectedAccount" :disabled="loading">
              <option v-if="loading" :value="null">Loading your Stripe accounts…</option>
              <option v-else>*** Add a Stripe Account</option>
              <option v-for="userAcc in filteredUserAccounts" :value="userAcc"
                :key="userAcc.attributes['account-rally-id']">
                {{ userAcc.attributes['title'] }}
              </option>
            </select>
            <p v-if="!loading && !importSelected" style="margin-top: 8px; color: #525151;">
              Or create a new Stripe account below.
            </p>
          </div>

          <div v-if="!importSelected" style="margin-top: 25px;">
            <div class="form-group">
              <p><strong>Register as an individual or a company (company requires an EIN number)?</strong></p>
              <div class="form-group inline-labels radios">
                <div class="form-group inline-labels radios">
                  <label>
                    <input v-model="account.attributes['entity']" type="radio" name="entity" value="individual" />
                    <div class="circle"></div>
                    Individual
                  </label>
                  <label>
                    <input v-model="account.attributes['entity']" type="radio" name="entity" value="company" />
                    <div class="circle"></div>
                    Company
                  </label>
                </div>
              </div>
              <span v-show="errorMessages.entity" class="dg error-text">{{ errorMessages.entity }}</span>
            </div>

            <div class="form-group">
              <label>Account Name*</label>
              <input
                v-model="account.attributes['title']"
                placeholder="Ex: Volunteer Fundraising"
                type="text"
                :class="['dg', { 'input-error': errorMessages.title }]"
              />
              <span v-show="errorMessages.title" class="dg error-text">{{ errorMessages.title }}</span>
            </div>

            <div class="billing-state-ctr">
              <div class="form-group">
                <label>Billing Zip*</label>
                <input
                  v-model="account.attributes['zip']"
                  type="text"
                  :class="['dg', { 'input-error': errorMessages.zip }]"
                />
                <span v-show="errorMessages.zip" class="dg error-text">{{ errorMessages.zip }}</span>
              </div>
              <div class="form-group">
                <label>State*</label>
                <StateDropDown
                  v-model="account.attributes['state']"
                  id="state-select"
                  :class="['dg', { 'input-error': errorMessages.state }]"
                />
                <span v-show="errorMessages.state" class="dg error-text">{{ errorMessages.state }}</span>
              </div>
            </div>
          </div>

          <div class="actions" style="padding-bottom:25px;">
            <button class="secondary" @click.prevent="closeModal">Cancel</button>
            <AsyncButton loadingText="Loading" :classes='["primary"]' v-if="!importSelected"
              :action="createAccount">Continue</AsyncButton>
            <AsyncButton :classes='["primary"]' v-else :action="importAccount">Import</AsyncButton>
          </div>

        </form>
      </div>
    </div>
  </div>
</template>


<script>
import { mapActions, mapState } from "vuex";
import StateDropDown from "@/components/shared/StateDropDown.vue";
import { userInfo } from "@/common/user-info";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import AsyncButton from "../AsyncButton.vue";

export default {
  name: "StripeSetupModal",
  components: {
    StateDropDown,
    AsyncButton
  },
  props: ['rallyId', 'isDesktopSize'],
  data: function () {
    return {
      modalStatus: false,
      errorMessage: '',
      creating: false,
      account: { attributes: {} },
      selectedAccount: null,
      userAccountsLoaded: false,
      rallyAccountsLoaded: false,
      loading: true,
      isRunningInCapacitor: false,
      errorMessages: {
        entity: '',
        title: '',
        zip: '',
        state: ''
      }
    };
  },
  computed: {
    ...mapState("rfins", ["accountsByRallyId", "accountsByUserId"]),
    ...mapState("rallies", ["allRalliesByRallyId"]),
    isAdmin() {
      return (this.myRole === "Admin" || this.myRole === "Owner");
    },
    // Prop when the opener passed one, else the route — every rfins call
    // below needs a real rally id, so never trust the prop alone.
    // An existing account (not the "*** Add" sentinel) is chosen in the picker.
    importSelected() {
      return !!this.selectedAccount && this.selectedAccount !== '*** Add a Stripe Account';
    },
    effectiveRallyId() {
      return parseInt(this.rallyId || this.$route.params.rallyId);
    },
    selectedRally() {
      return this.allRalliesByRallyId[this.effectiveRallyId];
    },
    myRole() {
      return this.selectedRally && this.selectedRally.membership
        ? this.selectedRally.membership.attributes["role"]
        : "Friend";
    },
    showStripe() {
      return this.allRalliesByRallyId[this.theRallyId]
        ? !this.allRalliesByRallyId[this.theRallyId].rally.attributes["hide-stripe"]
        : true;
    },
    theRallyId() {
      return this.$route.params.rallyId;
    },
    rallyAccounts() {
      if (this.rallyAccountsLoaded && this.accountsByRallyId[parseInt(this.$route.params.rallyId)]) {
        return this.accountsByRallyId[parseInt(this.$route.params.rallyId)].filter((acc) => {
          return (
            acc.attributes['account-type'] !== 'default' &&
            (this.isAdmin || (acc.attributes && acc.attributes["creator-id"] == userInfo().user_id))
          );
        });
      }
      return [];
    },
    filteredUserAccounts() {
      if (this.userAccountsLoaded && this.rallyAccountsLoaded) {
        if (this.rallyAccounts === undefined) {
          return this.accountsByUserId[userInfo().id].filter((userAccount) => {
            return userAccount.attributes['disabled'] !== true;
          });
        } else {
          return this.accountsByUserId[userInfo().id].filter((userAccount) => {
            return (
              userAccount.attributes['disabled'] !== true &&
              !this.rallyAccounts.some(
                (rallyAccount) =>
                  rallyAccount.attributes['master-account-id'] === userAccount.attributes['master-account-id']
              )
            );
          });
        }
      }
      return [];
    }
  },
  watch: {
    'account.attributes.entity'() {
      this.errorMessages.entity = this.account.attributes['entity']
        ? ""
        : "Please select registration type";
    },
    'account.attributes.title'() {
      this.errorMessages.title = (this.account.attributes['title'] && this.account.attributes['title'].trim())
        ? ""
        : "Please enter account name";
    },
    'account.attributes.zip'() {
      this.errorMessages.zip = (this.account.attributes['zip'] && this.account.attributes['zip'].trim())
        ? ""
        : "Please enter billing zip";
    },
    'account.attributes.state'() {
      this.errorMessages.state = this.account.attributes['state']
        ? ""
        : "Please select state";
    }
  },
  mounted() {
    this.isRunningInCapacitor = Capacitor.getPlatform() === "android" || Capacitor.getPlatform() === "ios";
    this.loadData();
  },
  methods: {
    ...mapActions("rfins", [
      "fetchAccounts",
      "fetchAccountById",
      "fetchUserAccounts",
      "createStripeAccount",
      "importStripeAccount"
    ]),
    ...mapActions("user", ["setModalProps"]),
    reloadPage() {
      window.location.reload();
    },
    async loadData() {
      try {
        await Promise.all([
          this.fetchAccounts({ rallyId: parseInt(this.$route.params.rallyId), returnUrl: window.location.href }),
          this.fetchUserAccounts({ returnUrl: window.location.href })
        ]);
      } catch (e) {
        console.error(e);
      }
      this.rallyAccountsLoaded = true;
      this.userAccountsLoaded = true;
      this.loading = false;
    },
    isAccountComplete(acc) {
      return acc.attributes && acc.attributes['stripe-status'] === 'enabled';
    },
    isAccountDisabled(acc) {
      return Boolean(acc.attributes && acc.attributes['disabled']);
    },
    isExpress(acc) {
      return acc.attributes && acc.attributes['account-type'] === 'stripe';
    },
    isStandard(acc) {
      return acc.attributes && acc.attributes['account-type'] === 'stripe_standard';
    },
    getAccountTitle(acc) {
      if (!acc || !acc.attributes) return "Bank Account";
      let title = acc.attributes['title'] || "Bank Account";
      if (this.isExpress(acc)) {
        title = "(E) - " + title;
      }
      if (this.isAccountDisabled(acc)) {
        title = title + " (Disabled)";
      }
      return title;
    },
    getStripeLinkText(acc) {
      if (this.isExpress(acc)) {
        return "Open Stripe Dashboard";
      }
      return "Login to Stripe";
    },
    openLoginUrl(acc) {
      let url = "https://dashboard.stripe.com/login";
      if (this.isExpress(acc) && acc.attributes && acc.attributes['connect-dashboard-link']) {
        url = acc.attributes['connect-dashboard-link']['url'] || url;
      }

      if (this.isRunningInCapacitor) {
        Browser.open({ url });
      } else {
        window.open(url, "_blank");
      }
    },
    continueEnrollment(acc) {
      const url = acc.attributes ? acc.attributes['stripe-standard-enrollment-url'] : null;
      if (!url) return;
      if (this.isRunningInCapacitor) {
        Browser.open({ url });
      } else {
        window.location.href = url;
      }
    },
    openDownloadModal(acc) {
      this.setModalProps({
        name: "DownloadReportsModal",
        account: acc,
        rallyId: parseInt(this.rallyId || this.$route.params.rallyId)
      });
    },
    closeModal() {
      this.setModalProps({})
    },
    handleSubmit() { },
    validateForm() {
      this.errorMessages = { entity: '', title: '', zip: '', state: '' };

      if (!this.account.attributes['entity']) {
        this.errorMessages.entity = "Please select registration type";
      }
      if (!this.account.attributes['title'] || !this.account.attributes['title'].trim()) {
        this.errorMessages.title = "Please enter account name";
      }
      if (!this.account.attributes['zip'] || !this.account.attributes['zip'].trim()) {
        this.errorMessages.zip = "Please enter billing zip";
      }
      if (!this.account.attributes['state'] || !this.account.attributes['state'].trim()) {
        this.errorMessages.state = "Please select state";
      }

      return Object.values(this.errorMessages).every((msg) => !msg);
    },
    async createAccount() {
      this.errorMessage = '';
      if (!this.validateForm()) {
        return;
      }
      if (this.creating) {
        return;
      }
      this.creating = true;
      const theReturnUrl = this.isRunningInCapacitor
        ? 'https://rallyhood.com/stripe_close.html'
        : window.location.href;
      const newAccount = await this.createStripeAccount({
        rallyId: this.effectiveRallyId,
        account: this.account,
        returnUrl: theReturnUrl
      });
      const fetchedNewAccount = await this.fetchAccountById({ id: newAccount.id, returnUrl: theReturnUrl });
      this.creating = false;
      try {
        const stripeUrl = fetchedNewAccount.attributes['stripe-standard-enrollment-url'];
        if (this.isRunningInCapacitor) {
          Browser.open({ url: stripeUrl });
          Browser.addListener('browserFinished', () => {
            window.location.reload();
          });
        } else {
          window.location.href = stripeUrl;
        }
      } catch (error) {
        console.error("ERROR RETRIEVING STRIPE ACCOUNT:", error);
        this.errorMessage = "Failed to retrieve Stripe account.";
        alert("ERROR RETRIEVING STRIPE ACCOUNT:", error)
      }
      this.creating = false;
    },
    async importAccount() {
      console.log("import account!!!")
      this.errorMessage = '';
      const selectedAccount = this.selectedAccount;
      const imported = await this.importStripeAccount({
        rallyId: this.effectiveRallyId,
        account: selectedAccount,
        returnUrl: window.location.href
      });
      if (!imported) {
        // The store returns null on any failure; don't report success for it.
        this.errorMessage = "We couldn't import that account. Please try again.";
        this.$store.dispatch('user/flashFailure', 'Account import failed.');
        return;
      }
      this.$store.dispatch('user/flashSuccess', 'Account successfully imported!');
      this.closeModal();
      this.fetchAccounts({ rallyId: this.effectiveRallyId, returnUrl: window.location.href });
    }
  }
};
</script>
